import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { useLibraryWorkouts } from './useLibraryWorkouts';
import { useLibraryWorkoutPlans } from './useLibraryWorkoutPlans';
import { useLibraryExercises } from './useLibraryExercises';
import { useLibraryLoader } from './useLibraryLoader';
import { useTrainerDashboardNavigation } from './useTrainerDashboardNavigation';
import { useWorkoutLibraryModals } from './useWorkoutLibraryModals';
import { useExerciseOverlayStack } from './useExerciseOverlayStack';
import { useAssignMembersCache } from './useAssignMembersCache';
import { useAssignWorkoutPlanModal } from './useAssignWorkoutPlanModal';
import { useAssignWorkoutModal } from './useAssignWorkoutModal';
import { useLibraryMutations } from './useLibraryMutations';
import { ExerciseService, warmupExerciseDB } from '../../../../lib/exerciseService';
import { PAGE_CHROME_LIGHT, PAGE_CHROME_OFF_WHITE } from '../../../../constants/pageChrome';
import { usePageStatusBar } from '../../../../hooks/usePageStatusBar';
import { accountNavDisplayName, greetingFirstName, trainerInitials } from '../../../../pages/account/accountShared';

/** Thin composer — sub-hooks assemble TrainerDashboardApi (P15-R6). */
export function useTrainerDashboard() {
  const { profile, user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeSection, setActiveSection, onSectionSelect } = useTrainerDashboardNavigation(
    location,
    navigate,
  );

  const statusBarTop =
    activeSection === 'members' || activeSection === 'library'
      ? PAGE_CHROME_LIGHT
      : PAGE_CHROME_OFF_WHITE;
  usePageStatusBar({ background: statusBarTop });

  const lib = useLibraryLoader({ profileId: profile?.id, activeSection });
  const {
    activeTab,
    setActiveTab,
    workouts,
    setWorkouts,
    workoutPlans,
    setWorkoutPlans,
    exercises,
    setExercises,
    loadingLibrary,
    exerciseLoadError,
    loadLibraryContent,
    loadAllExercisesForLibrary,
    libTabCount,
    hydrateListsFromSessionCache,
  } = lib;

  const wk = useLibraryWorkouts({ workouts });
  const pl = useLibraryWorkoutPlans({ workoutPlans });
  const ex = useLibraryExercises({ exercises, trainerId: profile?.id });

  const mutations = useLibraryMutations({
    profileId: profile?.id,
    workouts,
    setWorkouts,
    workoutPlans,
    setWorkoutPlans,
    exercises,
    setExercises,
    loadLibraryContent,
    loadAllExercisesForLibrary,
    navigate,
    wk,
    pl,
    ex,
  });

  const workoutModals = useWorkoutLibraryModals({
    workouts,
    navigate,
    setOpenWorkoutMenu: mutations.setOpenWorkoutMenu,
  });

  const exerciseStack = useExerciseOverlayStack({
    activeSection,
    activeTab,
    loadAllExercisesForLibrary,
    ex,
  });

  const [pendingOpenAddMember, setPendingOpenAddMember] = useState(false);

  const assignMembersCache = useAssignMembersCache(profile?.id);
  const onNavigateToAddMember = () => {
    setPendingOpenAddMember(true);
    setActiveSection('members');
  };

  const assignPlan = useAssignWorkoutPlanModal({
    profileId: profile?.id,
    workoutPlans,
    loadLibraryContent,
    setOpenWorkoutPlanMenu: mutations.setOpenWorkoutPlanMenu,
    onNavigateToAddMember,
    assignMembersCache,
  });

  const assignWorkout = useAssignWorkoutModal({
    workouts,
    setOpenWorkoutMenu: mutations.setOpenWorkoutMenu,
    onNavigateToAddMember,
    assignMembersCache,
  });

  const {
    editingExerciseRef,
    setEditingExercise,
    setExerciseCompanionSheetOpen,
    setViewingExercise,
    setDrawerIsEdit,
  } = exerciseStack;

  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const createMenuRef = useRef<HTMLDivElement>(null);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(event.target as Node)) {
        setIsCreateMenuOpen(false);
      }
    };
    if (isCreateMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isCreateMenuOpen]);

  const [workoutCreatedToast, setWorkoutCreatedToast] = useState<string | null>(null);
  const [planCreatedToast, setPlanCreatedToast] = useState<string | null>(null);

  const prevStateRef = useRef<typeof location.state>(undefined);
  useEffect(() => {
    const state = location.state as {
      openLibraryExercises?: boolean;
      editExerciseId?: string;
      viewExerciseId?: string;
      workoutCreated?: boolean;
      workoutName?: string;
      planCreated?: boolean;
      planName?: string;
    } | undefined;
    if (state?.planCreated && state.planName && state !== prevStateRef.current) {
      prevStateRef.current = state;
      setActiveSection('library');
      setActiveTab('workoutPlans');
      setPlanCreatedToast(state.planName);
      void loadLibraryContent({ silent: true });
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (state?.workoutCreated && state.workoutName && state !== prevStateRef.current) {
      prevStateRef.current = state;
      setActiveSection('library');
      setActiveTab('workouts');
      setWorkoutCreatedToast(state.workoutName);
      void loadLibraryContent({ silent: true });
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (state?.openLibraryExercises && state !== prevStateRef.current) {
      prevStateRef.current = state;
      setActiveSection('library');
      setActiveTab('exercises');
      hydrateListsFromSessionCache();
      navigate(location.pathname, { replace: true, state: {} });
      if (state.viewExerciseId) {
        ExerciseService.getExerciseById(state.viewExerciseId)
          .then((exercise) => {
            if (exercise) {
              setViewingExercise(exercise);
              setDrawerIsEdit(false);
            }
          })
          .catch(() => {});
      }
      if (state.editExerciseId) {
        ExerciseService.getExerciseById(state.editExerciseId)
          .then((exercise) => {
            if (exercise) {
              editingExerciseRef.current = exercise;
              setEditingExercise(exercise);
              setExerciseCompanionSheetOpen(true);
            }
          })
          .catch(() => {});
      }
    }
  }, [
    location.state,
    location.pathname,
    navigate,
    hydrateListsFromSessionCache,
    setActiveSection,
    setActiveTab,
    setViewingExercise,
    setDrawerIsEdit,
    editingExerciseRef,
    setEditingExercise,
    setExerciseCompanionSheetOpen,
    loadLibraryContent,
  ]);

  useEffect(() => {
    if (!user?.id) return;
    warmupExerciseDB(user.id);
  }, [user?.id]);

  const trainerDisplayName = accountNavDisplayName(profile, user, 'You');
  const trainerGreetingFirstName = greetingFirstName(profile, user);
  const trainerInitialsLabel = trainerInitials(profile, user);

  const libPrimaryCta = (() => {
    if (activeTab === 'workouts') {
      return { label: 'Create workout', onClick: () => workoutModals.handleCreateNewWorkout() };
    }
    if (activeTab === 'workoutPlans') {
      return { label: 'Create plan', onClick: () => navigate('/trainer-dashboard/workout-plan/new') };
    }
    return { label: 'Create exercise', onClick: exerciseStack.openCreateExerciseDrawer };
  })();

  return {
    profile,
    user,
    signOut,
    navigate,
    activeSection,
    setActiveSection,
    onSectionSelect,
    activeTab,
    setActiveTab,
    workouts,
    setWorkouts,
    workoutPlans,
    setWorkoutPlans,
    exercises,
    setExercises,
    loadingLibrary,
    exerciseLoadError,
    loadLibraryContent,
    loadAllExercisesForLibrary,
    libTabCount,
    wk,
    pl,
    ex,
    handleSignOut,
    trainerDisplayName,
    trainerGreetingFirstName,
    trainerInitialsLabel,
    libPrimaryCta,
    isCreateMenuOpen,
    setIsCreateMenuOpen,
    createMenuRef,
    ...workoutModals,
    ...exerciseStack,
    ...assignPlan,
    ...assignWorkout,
    pendingOpenAddMember,
    setPendingOpenAddMember,
    workoutCreatedToast,
    setWorkoutCreatedToast,
    planCreatedToast,
    setPlanCreatedToast,
    ...mutations,
  };
}

export type TrainerDashboardApi = ReturnType<typeof useTrainerDashboard>;
